import { useParams, Link } from 'react-router-dom';
import ComicCard from '@/components/feature/ComicCard';
import { comics, categories } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comicId = parseInt(id || '1', 10);
  const comic = comics.find((c) => c.id === comicId);

  if (!comic) {
    return (
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
        <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100">
          <i className="ri-error-warning-line text-3xl text-secondary-400"></i>
        </div>
        <h1 className="font-heading font-bold text-xl text-foreground-950 mb-2">漫画未找到</h1>
        <p className="text-sm text-foreground-500 mb-6">抱歉，您查找的漫画不存在或已下架</p>
        <Link to="/category/all" className="inline-flex px-6 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap">
          浏览全部漫画
        </Link>
      </main>
    );
  }

  const relatedComics = comics.filter((c) => c.id !== comic.id && (c.category === comic.category || c.tags.some((t) => comic.tags.includes(t)))).slice(0, 6);
  const categoryName = categories.find((c) => c.slug === comic.category)?.name || comic.category;
  const formatViews = (v: number) => {
    if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
    return v.toString();
  };

  const chapterList = Array.from({ length: Math.min(comic.chapters, 20) }, (_, i) => ({
    num: comic.chapters - i,
    title: `第${comic.chapters - i}话`,
    date: new Date(comic.updateDate).getTime() - i * 7 * 24 * 60 * 60 * 1000,
  }));

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Book',
    name: comic.title,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    description: comic.description,
    url: `https://mousehole.cn/comic/${comic.id}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '皮皮漫画网',
      url: 'https://mousehole.cn',
    },
  };

  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `${comic.title}是免费的吗？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的，皮皮漫画网提供完全免费的韩漫在线阅读服务，《' + comic.title + '》可以免费阅读全部章节。',
        },
      },
      {
        '@type': 'Question',
        name: `${comic.title}多久更新一次？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `《${comic.title}》保持稳定的更新频率，最新章节会第一时间在皮皮漫画网同步发布。`,
        },
      },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(faqLd)}</script>

      <main>
        {/* Comic Header */}
        <section className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-10">
            <div className="flex flex-col md:flex-row gap-6 md:gap-8">
              {/* Cover */}
              <div className="flex-shrink-0 w-40 md:w-56 mx-auto md:mx-0">
                <div className="aspect-[3/4] rounded-lg overflow-hidden border border-background-200/70">
                  <img
                    src={comic.cover}
                    alt={`${comic.title} - 皮皮漫画网韩漫在线阅读`}
                    title={`${comic.title} 免费韩国漫画下拉式阅读`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              {/* Info */}
              <div className="flex-1">
                <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-3">
                  <strong>{comic.title}</strong>
                </h1>
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <div className="flex items-center gap-1 bg-accent-100 text-accent-700 px-2 py-0.5 rounded text-sm font-medium">
                    <i className="ri-star-fill text-xs"></i>
                    {comic.rating}
                  </div>
                  <span className="text-sm text-foreground-500">作者：{comic.author}</span>
                  <span className="text-sm text-foreground-500">{formatViews(comic.views)} 次阅读</span>
                  <span className={`text-sm px-2 py-0.5 rounded-full font-medium ${comic.status === '连载中' ? 'bg-primary-100 text-primary-700' : 'bg-secondary-100 text-secondary-700'}`}>
                    {comic.status}
                  </span>
                  <span className="text-sm text-foreground-500">共{comic.chapters}话</span>
                </div>

                <div className="flex flex-wrap gap-1.5 mb-4">
                  <Link
                    to={`/category/${comic.category}`}
                    className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 hover:bg-secondary-200 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                  >
                    {categoryName}
                  </Link>
                  {comic.tags.map((tag) => (
                    <span key={tag} className="text-xs px-2.5 py-1 rounded-full bg-background-50 border border-background-200/70 text-foreground-600 whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>

                <p className="text-sm text-foreground-600 leading-relaxed mb-6">{comic.description}</p>

                <div className="flex flex-wrap gap-3">
                  <Link
                    to={`/comic/${comic.id}#chapters`}
                    className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
                  >
                    开始阅读
                  </Link>
                  <button className="px-6 py-2.5 rounded-full border border-background-200/70 text-foreground-700 text-sm font-medium hover:bg-background-50 transition-all duration-200 cursor-pointer whitespace-nowrap">
                    <i className="ri-heart-line mr-1.5"></i>收藏
                  </button>
                  <button className="px-6 py-2.5 rounded-full border border-background-200/70 text-foreground-700 text-sm font-medium hover:bg-background-50 transition-all duration-200 cursor-pointer whitespace-nowrap">
                    <i className="ri-share-line mr-1.5"></i>分享
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Chapters */}
        <section id="chapters" className="max-w-7xl mx-auto px-4 md:px-6 py-10">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-1 h-6 rounded-full bg-primary-500"></div>
              <h2 className="font-heading font-bold text-xl text-foreground-950">章节列表</h2>
            </div>
            <span className="text-sm text-foreground-500">共{comic.chapters}话</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {chapterList.map((ch) => (
              <a
                key={ch.num}
                href="#"
                className="flex items-center justify-between p-3.5 rounded-lg border border-background-200/70 bg-background-50 hover:border-primary-300 hover:bg-primary-50/50 transition-all duration-200 cursor-pointer group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-100 group-hover:bg-primary-100 transition-colors duration-200">
                    <i className="ri-bookmark-line text-secondary-500 group-hover:text-primary-500 text-sm transition-colors duration-200"></i>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-950 group-hover:text-primary-600 transition-colors duration-200 whitespace-nowrap">
                      {ch.title}
                    </div>
                    <div className="text-xs text-foreground-400">
                      {new Date(ch.date).toLocaleDateString('zh-CN')}
                    </div>
                  </div>
                </div>
                <i className="ri-arrow-right-s-line text-foreground-300 group-hover:text-primary-400 transition-colors duration-200"></i>
              </a>
            ))}
          </div>

          {comic.chapters > 20 && (
            <div className="text-center mt-6">
              <button className="px-8 py-2.5 rounded-full border border-background-200/70 text-foreground-600 text-sm hover:bg-background-50 transition-all duration-200 cursor-pointer whitespace-nowrap">
                查看全部{comic.chapters}话
              </button>
            </div>
          )}
        </section>

        {/* Related Comics */}
        {relatedComics.length > 0 && (
          <section className="bg-background-100 py-10">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-6 rounded-full bg-accent-500"></div>
                <h2 className="font-heading font-bold text-xl text-foreground-950">相关推荐</h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {relatedComics.map((c) => (
                  <ComicCard key={c.id} {...c} />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* FAQ */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 py-10">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-6 rounded-full bg-primary-500"></div>
            <h2 className="font-heading font-bold text-xl text-foreground-950">常见问题</h2>
          </div>
          <div className="space-y-3 max-w-3xl">
            <div className="p-4 rounded-lg bg-background-50 border border-background-200/70">
              <h3 className="font-medium text-sm text-foreground-950 mb-2">
                《{comic.title}》是免费的吗？
              </h3>
              <p className="text-sm text-foreground-600">
                是的，皮皮漫画网提供完全免费的韩漫在线阅读服务，《{comic.title}》可以免费阅读全部章节。
              </p>
            </div>
            <div className="p-4 rounded-lg bg-background-50 border border-background-200/70">
              <h3 className="font-medium text-sm text-foreground-950 mb-2">
                《{comic.title}》多久更新一次？
              </h3>
              <p className="text-sm text-foreground-600">
                《{comic.title}》保持稳定的更新频率，最新章节会第一时间在皮皮漫画网同步发布，最新更新日期为 {comic.updateDate}。
              </p>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}